import React, { useCallback } from 'react';
import {
  Plus,
  Trash,
  TreeStructure,
} from '@phosphor-icons/react';
import {
  SegmentExpression,
  SegmentExpressionGroup,
  SegmentClusterCondition,
  isSegmentClusterCondition,
} from 'shared/types/segment';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';

// ============================================
// Types
// ============================================

interface ClusterForPicker {
  id: number;
  name: string;
  type?: string;
  buildingCount?: number;
  buildings?: Array<{ id: string }>;
}

interface SegmentExpressionBuilderProps {
  value: SegmentExpression | null;
  onChange: (expr: SegmentExpression) => void;
  clusters: ClusterForPicker[];
}

// ============================================
// Helpers
// ============================================

function createEmptyGroup(operator: 'AND' | 'OR' = 'AND'): SegmentExpressionGroup {
  return {
    type: 'group',
    logicalOperator: operator,
    conditions: [],
  };
}

function createClusterCondition(clusterId: number): SegmentClusterCondition {
  return {
    type: 'cluster',
    clusterId,
    negate: false,
  };
}

// ============================================
// Cluster Condition Row
// ============================================

interface ClusterConditionRowProps {
  condition: SegmentClusterCondition;
  clusters: ClusterForPicker[];
  onChange: (updated: SegmentClusterCondition) => void;
  onRemove: () => void;
}

const ClusterConditionRow: React.FC<ClusterConditionRowProps> = ({
  condition,
  clusters,
  onChange,
  onRemove,
}) => {
  const selectedCluster = clusters.find((c) => c.id === condition.clusterId);

  return (
    <div className="flex items-center gap-2 p-2 rounded-lg bg-white border">
      <Select
        value={condition.negate ? 'exclude' : 'include'}
        onValueChange={(val) =>
          onChange({ ...condition, negate: val === 'exclude' })
        }
      >
        <SelectTrigger className="w-[130px] h-8 text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="include">Inclusief</SelectItem>
          <SelectItem value="exclude">Exclusief</SelectItem>
        </SelectContent>
      </Select>

      <Select
        value={condition.clusterId ? String(condition.clusterId) : ''}
        onValueChange={(val) =>
          onChange({ ...condition, clusterId: Number(val) })
        }
      >
        <SelectTrigger className="flex-1 h-8 text-sm">
          <SelectValue placeholder="Selecteer cluster..." />
        </SelectTrigger>
        <SelectContent>
          {clusters.map((cluster) => (
            <SelectItem key={cluster.id} value={String(cluster.id)}>
              <div className="flex items-center gap-2">
                <span>{cluster.name}</span>
                {cluster.type && (
                  <Badge
                    variant="secondary"
                    className="text-[10px] px-1 py-0 h-4"
                  >
                    {cluster.type === 'FILTER' ? 'Filter' : 'Handmatig'}
                  </Badge>
                )}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {selectedCluster && (
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {selectedCluster.buildingCount ??
            selectedCluster.buildings?.length ??
            0}{' '}
          geb.
        </span>
      )}

      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-muted-foreground hover:text-destructive flex-shrink-0"
        onClick={onRemove}
      >
        <Trash className="size-4" />
      </Button>
    </div>
  );
};

// ============================================
// Expression Group Component (recursive)
// ============================================

interface ExpressionGroupProps {
  group: SegmentExpressionGroup;
  clusters: ClusterForPicker[];
  onChange: (updated: SegmentExpressionGroup) => void;
  onRemove?: () => void;
  depth?: number;
}

const ExpressionGroupComponent: React.FC<ExpressionGroupProps> = ({
  group,
  clusters,
  onChange,
  onRemove,
  depth = 0,
}) => {
  const bgColor =
    group.logicalOperator === 'AND'
      ? 'bg-moss-secondary-light/30 border-moss-primary/20'
      : 'bg-brick-secondary-fine/30 border-brick-primary/20';

  const operatorColor =
    group.logicalOperator === 'AND' ? 'text-moss-primary' : 'text-brick-primary';

  const handleConditionChange = useCallback(
    (index: number, updated: SegmentClusterCondition | SegmentExpressionGroup) => {
      const newConditions = [...group.conditions];
      newConditions[index] = updated;
      onChange({ ...group, conditions: newConditions });
    },
    [group, onChange],
  );

  const handleRemoveCondition = useCallback(
    (index: number) => {
      const newConditions = group.conditions.filter((_, i) => i !== index);
      onChange({ ...group, conditions: newConditions });
    },
    [group, onChange],
  );

  const handleAddCluster = useCallback(() => {
    const firstAvailableCluster = clusters[0];
    if (!firstAvailableCluster) return;
    onChange({
      ...group,
      conditions: [
        ...group.conditions,
        createClusterCondition(firstAvailableCluster.id),
      ],
    });
  }, [group, clusters, onChange]);

  const handleAddGroup = useCallback(() => {
    const oppositeOperator = group.logicalOperator === 'AND' ? 'OR' : 'AND';
    onChange({
      ...group,
      conditions: [...group.conditions, createEmptyGroup(oppositeOperator)],
    });
  }, [group, onChange]);

  const handleToggleOperator = useCallback(() => {
    onChange({
      ...group,
      logicalOperator: group.logicalOperator === 'AND' ? 'OR' : 'AND',
    });
  }, [group, onChange]);

  return (
    <div className={`rounded-lg border p-3 space-y-2 ${bgColor}`}>
      {/* Group header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className={`h-7 text-xs font-semibold ${operatorColor}`}
            onClick={handleToggleOperator}
          >
            {group.logicalOperator === 'AND' ? 'EN (AND)' : 'OF (OR)'}
          </Button>
          <span className="text-xs text-muted-foreground">
            {group.conditions.length === 0
              ? 'Lege groep'
              : `${group.conditions.length} ${group.conditions.length === 1 ? 'voorwaarde' : 'voorwaarden'}`}
          </span>
        </div>
        {onRemove && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-destructive"
            onClick={onRemove}
          >
            <Trash className="size-3.5" />
          </Button>
        )}
      </div>

      {/* Conditions */}
      {group.conditions.map((condition, index) => (
        <React.Fragment key={index}>
          {index > 0 && (
            <div className="flex items-center gap-2 pl-2">
              <span className={`text-xs font-medium ${operatorColor}`}>
                {group.logicalOperator === 'AND' ? 'EN' : 'OF'}
              </span>
              <div className="flex-1 border-t border-dashed" />
            </div>
          )}
          {isSegmentClusterCondition(condition) ? (
            <ClusterConditionRow
              condition={condition}
              clusters={clusters}
              onChange={(updated) => handleConditionChange(index, updated)}
              onRemove={() => handleRemoveCondition(index)}
            />
          ) : (
            depth < 2 && (
              <ExpressionGroupComponent
                group={condition}
                clusters={clusters}
                onChange={(updated) => handleConditionChange(index, updated)}
                onRemove={() => handleRemoveCondition(index)}
                depth={depth + 1}
              />
            )
          )}
        </React.Fragment>
      ))}

      {/* Add buttons */}
      <div className="flex items-center gap-2 pt-1">
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs"
          onClick={handleAddCluster}
          disabled={clusters.length === 0}
        >
          <Plus className="size-3" />
          Cluster
        </Button>
        {depth < 2 && (
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={handleAddGroup}
          >
            <TreeStructure className="size-3" />
            Groep ({group.logicalOperator === 'AND' ? 'OF' : 'EN'})
          </Button>
        )}
      </div>
    </div>
  );
};

// ============================================
// Main Component
// ============================================

const SegmentExpressionBuilder: React.FC<SegmentExpressionBuilderProps> = ({
  value,
  onChange,
  clusters,
}) => {
  const expression = value || createEmptyGroup('AND');

  if (clusters.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 rounded-lg border-2 border-dashed border-border/50">
        <TreeStructure
          className="size-10 text-muted-foreground/50 mb-3"
          weight="duotone"
        />
        <p className="text-muted-foreground text-sm text-center">
          Geen clusters beschikbaar. Maak eerst clusters aan om een segment
          expressie te bouwen.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <ExpressionGroupComponent
        group={expression}
        clusters={clusters}
        onChange={onChange}
      />
    </div>
  );
};

export default SegmentExpressionBuilder;
